// src/store/vuex.js
import { createStore } from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import axiosInstance from '@/service/axiosInstance';
import alert from "./modules/alert";

export default createStore({
    state: {
        alertCount: 0, // ✅ 알람 개수
        alertList: [], // ✅ 알람 리스트 저장
        lastAlert: null, // ✅ 실시간 알림만 감지하기 위한 전용 state
        user: null,
        originalSerialKey: null, // 추가된 상태
        menuVisible: false, // 메뉴 상태 추가
        menuMoVisible: false, // 메뉴 상태 모바일
        menuSubMoVisible: false, // 서브메뉴 상태 모바일
        dialog: {
            isVisible: false,
            title: '',
            content: '',
            type: null, // alert, confirm
            onConfirm: null,
            onCancel: null,
            size: '',
            confirmText: '',
            confirmColor: '',
            cancelText: ''
        }
    },
    getters: {
        isAuthenticated: (state) => !!state.user,
        isMenuVisible: (state) => state.menuVisible // 메뉴 상태 반환
    },
    mutations: {
        setAlertType(state, type) {
            state.dialog.alertType = type;
        },
        clearAlertType(state) {
            state.dialog.alertType = null;
        },
        updateDialogContent(state, payload) {
            state.dialog.content = payload.content || '';
        },
        setLastAlert(state, alert) {
            state.lastAlert = alert;
        },
        // 변이(mutations) 정의
        setUser(state, user) {
            // 사용자 정보를 상태에 저장하는 뮤테이션
            state.user = user;
        },
        setOriginalSerialKey(state, key) {
            state.originalSerialKey = key; // 변이 추가
        },
        toggleMenuVisible(state) {
            state.menuVisible = !state.menuVisible; // 메뉴 상태 토글
        },
        toggleMenuMoVisible(state) {
            state.menuMoVisible = !state.menuMoVisible;
        },
        toggleMenuSubMoVisible(state) {
            state.menuSubMoVisible = !state.menuSubMoVisible;
        },
        openDialog(state, payload) {
            state.dialog.isVisible = true;
            state.dialog.title = payload.title || '';
            state.dialog.content = payload.content || '';
            state.dialog.type = payload.type || 'alert';
            state.dialog.onConfirm = payload.onConfirm || null;
            state.dialog.onCancel = payload.onCancel || null;
            state.dialog.size = payload.size || '';
            state.dialog.confirmText = payload.confirmText || '확인';
            state.dialog.confirmColor = payload.confirmColor || 'primary';
            state.dialog.cancelText = payload.cancelText || '취소';
        },
        closeDialog(state) {
            state.dialog.isVisible = false;
            state.dialog.title = '';
            state.dialog.content = '';
            state.dialog.type = null;
            state.dialog.onConfirm = null;
            state.dialog.onCancel = null;
            state.dialog.size = '';
            state.dialog.confirmText = '';
            state.dialog.confirmColor = '';
            state.dialog.cancelText = '';
        },
        updateAlertCount(state, count) {
            state.alertCount = count;
        },
        incrementAlertCount(state) {
            state.alertCount += 1;
        },
        resetAlertCount(state) {
            state.alertCount = 0;
        },
        addAlert(state, alert) {
            state.alertList.unshift(alert); // ✅ 최신 알람을 리스트 맨 앞에 추가
        },
        setAlertList(state, alerts) {
            state.alertList = alerts;
        }
    },
    actions: {
        loginSuccess({ commit }, responseData) {
            const user = responseData ? (typeof responseData === 'string' ? JSON.parse(responseData) : responseData) : null;
            commit('setUser', user);

            /*console.log("#####" + JSON.stringify({ user }));*/

            // 모듈 내에서 로컬 스토리지 업데이트 (불필요)
            //localStorage.setItem('vuex_state', JSON.stringify({ user }));
        },
        async hydrateSessionUser({ commit }) {
            try {
                const response = await axiosInstance.get('/api/web/v1/common/sessionCheck', {
                    withCredentials: true,
                });

                const user = response?.data?.userData ?? null;
                commit('setUser', user);
                return user;
            } catch (error) {
                commit('setUser', null);
                return null;
            }
        },
        logout({ commit }) {
            // 로그아웃 시 로컬 스토리지에서도 사용자 정보 제거(불필요
            //localStorage.removeItem('vuex_state');
            commit('setUser', null);

            // ✅ sessionCheck 캐시 무효화 (로그아웃 시 즉시 재검증 필요)
            // ✨ 불필요한 invalidateSessionCache 호출 삭제
            // if (typeof window !== 'undefined' && window.invalidateSessionCache) {
            //     window.invalidateSessionCache();
            // }
        },
        saveOriginalSerialKey({ commit }, key) {
            commit('setOriginalSerialKey', key); // 변이 커밋
        },
        toggleMenu({ commit }) {
            commit('toggleMenuVisible'); // 메뉴 토글 액션
        },
        toggleMenuMo({ commit }) {
            commit('toggleMenuMoVisible');
        },
        toggleMenuSubMo({ commit }) {
            commit('toggleMenuSubMoVisible');
        },
        openDialog({ commit }, payload) {
            commit('openDialog', payload);
        },
        closeDialog({ commit }) {
            commit('closeDialog');
        },
        confirmDialog({ state, commit }) {
            // onCinfrim 콜백이 존재하면 설정
            if (typeof state.dialog.onConfirm === 'function') {
                state.dialog.onConfirm();
            }
            commit('closeDialog');
        },
        cancelDialog({ state, commit }) {
            // onCancel 콜백이 존재하면 설정
            if (typeof state.dialog.onCancel === 'function') {
                state.dialog.onCancel();
            }
            commit('closeDialog');
        },
        updateAlertCount({ commit }, count) {
            commit('updateAlertCount', count);
        },
        incrementAlertCount({ commit }) {
            commit('incrementAlertCount');
        },
        resetAlertCount({ commit }) {
            commit('resetAlertCount');
        },
        addAlert({ commit }, alert) {
            commit('addAlert', alert); // alertList에 추가
            commit('setLastAlert', alert); // ✅ 실시간용 lastAlert 업데이트
        },
        setAlertList({ commit }, alerts) {
            commit('setAlertList', alerts);
        },
        // ✅ 알람 삭제 액션
        async removeAlert({ commit, state }, guid) {
            // ✅ Vuex에서 알람 리스트 업데이트
            const updatedAlerts = state.alertList.filter((alert) => alert.guid !== guid);
            commit('setAlertList', updatedAlerts);

            // ✅ 알람 개수 업데이트
            commit('updateAlertCount', updatedAlerts.length);
        },
        // ✅ 모든 알람 삭제 (Vuex 상태 업데이트)
        clearAllAlerts({ commit }) {
            commit('setAlertList', []); // 알람 리스트 초기화
            commit('updateAlertCount', 0); // 알람 개수 0으로 설정
            console.log('📌 Vuex: 모든 알람 삭제 완료');
        }
    },
    modules: {
        alert
        // 필요한 경우 모듈(modules) 추가
    },
    plugins: [
        createPersistedState({
            // 로컬 스토리지에 유지할 데이터만 화이트리스트로 관리합니다.
            // 함수가 포함된 dialog나 휘발성 UI 메뉴 상태는 제외하여 오동작을 방지합니다.
            paths: ['user', 'originalSerialKey']
        })

        // createPersistedState({
        //     reducer: (state) => ({
        //         user: state.user,
        //         originalSerialKey: state.originalSerialKey,
        //     })
        // })
    ] // vuex-persistedstate 플러그인 설정 적용
});